import { House, Map, MessageCircle, Palette, Search, Tag, Truck, Wrench } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Tab } from '@headlessui/react';
import { Button } from '@/components/Common/button';

import * as yaml from 'js-yaml';

interface PromptSection {
  system: string;
  user: string;
}

interface RagPromptsData {
  intent_classification: PromptSection;
  theme_classification: PromptSection;
  answer_synthesis: PromptSection;
  query_expansion: PromptSection;
  json_repair: PromptSection;
  query_planner: PromptSection;
  service_finder: PromptSection;
  traffic_data_lookup: PromptSection;
}

const PROMPT_CATEGORIES = [
  { key: 'intent_classification', labelKey: 'admin.configForms.ragPrompts.categories.intent', icon: Tag },
  { key: 'theme_classification', labelKey: 'admin.configForms.ragPrompts.categories.theme', icon: Palette },
  { key: 'answer_synthesis', labelKey: 'admin.configForms.ragPrompts.categories.answer', icon: MessageCircle },
  { key: 'query_expansion', labelKey: 'admin.configForms.ragPrompts.categories.expansion', icon: Search },
  { key: 'json_repair', labelKey: 'admin.configForms.ragPrompts.categories.jsonFix', icon: Wrench },
  { key: 'query_planner', labelKey: 'admin.configForms.ragPrompts.categories.planner', icon: Map },
  { key: 'service_finder', labelKey: 'admin.configForms.ragPrompts.categories.services', icon: House },
  { key: 'traffic_data_lookup', labelKey: 'admin.configForms.ragPrompts.categories.traffic', icon: Truck },
] as const;

interface RagPromptsFormProps {
  initialContent?: string;
  onSave: (content: string) => void;
  onCancel: () => void;
}

export function RagPromptsForm({ initialContent, onSave, onCancel }: RagPromptsFormProps) {
  const { t } = useTranslation();
  const [prompts, setPrompts] = useState<RagPromptsData>({
    intent_classification: { system: '', user: '' },
    theme_classification: { system: '', user: '' },
    answer_synthesis: { system: '', user: '' },
    query_expansion: { system: '', user: '' },
    json_repair: { system: '', user: '' },
    query_planner: { system: '', user: '' },
    service_finder: { system: '', user: '' },
    traffic_data_lookup: { system: '', user: '' },
  });

  useEffect(() => {
    if (initialContent) {
      try {
        const parsed = yaml.load(initialContent) as RagPromptsData;
        if (parsed) {
          setPrompts({
            intent_classification: parsed.intent_classification || { system: '', user: '' },
            theme_classification: parsed.theme_classification || { system: '', user: '' },
            answer_synthesis: parsed.answer_synthesis || { system: '', user: '' },
            query_expansion: parsed.query_expansion || { system: '', user: '' },
            json_repair: parsed.json_repair || { system: '', user: '' },
            query_planner: parsed.query_planner || { system: '', user: '' },
            service_finder: parsed.service_finder || { system: '', user: '' },
            traffic_data_lookup: parsed.traffic_data_lookup || { system: '', user: '' },
          });
        }
      } catch (error) {
        console.error('Error parsing RAG prompts YAML:', error);
      }
    }
  }, [initialContent]);

  const handleUpdatePrompt = (category: keyof RagPromptsData, field: 'system' | 'user', value: string) => {
    setPrompts({
      ...prompts,
      [category]: {
        ...prompts[category],
        [field]: value,
      },
    });
  };

  const handleSave = () => {
    const yamlContent = yaml.dump(prompts, {
      indent: 2,
      lineWidth: -1,
      noRefs: true,
      sortKeys: false,
      styles: {
        '!!str': 'literal',
      },
    });

    onSave(yamlContent);
  };

  return (
    <div className="space-y-4">
      <div className="pb-4 border-b border-border">
        <p className="text-sm text-muted-foreground">
          {t('admin.configForms.ragPrompts.intro')}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          {t('admin.configForms.shared.promptPairHint')}
        </p>
      </div>

      <Tab.Group>
        <Tab.List className="flex flex-wrap gap-1 rounded-xl bg-muted p-1">
          {PROMPT_CATEGORIES.map((category) => (
            <Tab
              key={category.key}
              className={({ selected }) =>
                `flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium leading-5 transition-all
                ${selected
                  ? 'bg-background text-foreground shadow'
                  : 'text-muted-foreground hover:bg-background/60 hover:text-foreground'
                }`
              }
            >
              <category.icon className="h-3.5 w-3.5" />
              {t(category.labelKey)}
            </Tab>
          ))}
        </Tab.List>

        <Tab.Panels className="mt-4">
          {PROMPT_CATEGORIES.map((category) => (
            <Tab.Panel key={category.key}>
              <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    {t('admin.configForms.shared.systemPrompt')}
                  </label>
                  <textarea
                    value={prompts[category.key].system}
                    onChange={(e) => handleUpdatePrompt(category.key, 'system', e.target.value)}
                    rows={10}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm resize-none font-mono"
                    placeholder={t('admin.configForms.shared.systemPromptFor', { label: t(category.labelKey) })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    {t('admin.configForms.shared.userPrompt')}
                  </label>
                  <textarea
                    value={prompts[category.key].user}
                    onChange={(e) => handleUpdatePrompt(category.key, 'user', e.target.value)}
                    rows={10}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm resize-none font-mono"
                    placeholder={t('admin.configForms.shared.userPromptFor', { label: t(category.labelKey) })}
                  />
                </div>
              </div>
            </Tab.Panel>
          ))}
        </Tab.Panels>
      </Tab.Group>

      <div className="flex gap-2 justify-end pt-4 border-t border-border">
        <Button onClick={onCancel} variant="outline">
          {t('actions.cancel')}
        </Button>
        <Button onClick={handleSave}>
          {t('actions.save')}
        </Button>
      </div>
    </div>
  );
}
